import React, { useState, useContext, useEffect } from "react";

import { TextField, Button, CssBaseline } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

/* ==========================================================================
   CRM LASIT - PANTALLA DE INICIO DE SESIÓN  (VERSIÓN PREMIUM v2.0)
   PROHIBIDO MODIFICAR SIN AUTORIZACIÓN  |  Trae AI  |  2026-05-13
   Reemplaza el antiguo login de "CRM Ultron" con diseño glassmorphism.
   ========================================================================== */

const useStyles = makeStyles(() => ({
  "@global": {
    "*": { boxSizing: "border-box", margin: 0, padding: 0 },
    body: {
      fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, Cantarell, 'Open Sans', Helvetica, Arial, sans-serif",
      overflow: "hidden",
    },

    // Animación de fondo: partículas flotantes
    "@keyframes float": {
      "0%, 100%": { transform: "translateY(0px) rotate(0deg)", opacity: 0.6 },
      "50%": { transform: "translateY(-30px) rotate(180deg)", opacity: 1 },
    },
    "@keyframes pulse": {
      "0%, 100%": { opacity: 0.3, transform: "scale(1)" },
      "50%": { opacity: 0.8, transform: "scale(1.08)" },
    },
    "@keyframes slideInRight": {
      "0%": { opacity: 0, transform: "translateX(60px)" },
      "100%": { opacity: 1, transform: "translateX(0)" },
    },
    "@keyframes fadeInUp": {
      "0%": { opacity: 0, transform: "translateY(20px)" },
      "100%": { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes shimmer": {
      "0%": { backgroundPosition: "-200% center" },
      "100%": { backgroundPosition: "200% center" },
    },
    "@keyframes nodeMove": {
      "0%": { transform: "translate(0, 0)" },
      "25%": { transform: "translate(15px, -20px)" },
      "50%": { transform: "translate(30px, 0)" },
      "75%": { transform: "translate(15px, 20px)" },
      "100%": { transform: "translate(0, 0)" },
    },
    "@keyframes rotateSlow": {
      "from": { transform: "rotate(0deg)" },
      "to": { transform: "rotate(360deg)" },
    },
    "@keyframes glowPulse": {
      "0%, 100%": { boxShadow: "0 0 20px 5px rgba(0, 212, 170, 0.2)" },
      "50%": { boxShadow: "0 0 40px 15px rgba(0, 212, 170, 0.5)" },
    },
  },

  root: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    background: "linear-gradient(135deg, #060A14 0%, #0A0E1A 40%, #0D1B2A 100%)",
    position: "relative",
    overflow: "hidden",
  },

  // --- Fondo animado: burbujas y nodos ---
  bgOrb1: {
    position: "absolute",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(0,212,170,0.12) 0%, transparent 70%)",
    top: "-150px",
    left: "10%",
    animation: "$pulse 8s ease-in-out infinite",
    pointerEvents: "none",
  },
  bgOrb2: {
    position: "absolute",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(0,100,255,0.1) 0%, transparent 70%)",
    bottom: "-100px",
    left: "30%",
    animation: "$pulse 10s ease-in-out infinite 2s",
    pointerEvents: "none",
  },
  bgOrb3: {
    position: "absolute",
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(0,212,170,0.08) 0%, transparent 70%)",
    top: "30%",
    left: "55%",
    animation: "$pulse 12s ease-in-out infinite 4s",
    pointerEvents: "none",
  },

  // Partículas flotantes
  particle: {
    position: "absolute",
    borderRadius: "50%",
    background: "rgba(0, 212, 170, 0.5)",
    pointerEvents: "none",
  },

  // Grid de puntos de fondo
  dotGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage: "radial-gradient(circle, rgba(0,212,170,0.15) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    opacity: 0.4,
    pointerEvents: "none",
  },

  // --- Panel Izquierdo ---
  leftPanel: {
    flex: "0 0 58%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 768px)": { display: "none" },
  },

  logoWrapper: {
    position: "relative",
    marginBottom: "40px",
    animation: "$fadeInUp 1s ease-out",
    // El wrapper define el tamaño del círculo visible
    width: "240px",
    height: "240px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoGlow: {
    position: "absolute",
    inset: "-20px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(0,212,170,0.25) 0%, transparent 70%)",
    animation: "$glowPulse 3s ease-in-out infinite",
  },
  logoRing: {
    position: "absolute",
    inset: "-30px",
    borderRadius: "50%",
    border: "1px solid rgba(0,212,170,0.25)",
    animation: "$rotateSlow 20s linear infinite",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-3px",
      left: "50%",
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      background: "#00D4AA",
      transform: "translateX(-50%)",
    },
  },
  // Círculo que contiene el escudo sin recortarlo
  logoCircle: {
    width: "240px",
    height: "240px",
    borderRadius: "50%",
    border: "2px solid rgba(0,212,170,0.45)",
    background: "rgba(5,10,20,0.6)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 2,
    boxShadow: "inset 0 0 30px rgba(0,212,170,0.08), 0 0 25px rgba(0,212,170,0.15)",
  },
  // La imagen NO tiene border-radius para no recortarse
  logoImg: {
    width: "78%",      // Imagen al 78% del círculo = escudo visible y con margen
    height: "78%",
    objectFit: "contain",
    borderRadius: 0,   // Sin recorte circular
    filter: "drop-shadow(0 0 18px rgba(0, 212, 170, 0.35))",
  },

  leftTitle: {
    fontSize: "2.2rem",
    fontWeight: 700,
    color: "#FFFFFF",
    textAlign: "center",
    lineHeight: 1.3,
    marginBottom: "16px",
    animation: "$fadeInUp 1s ease-out 0.3s both",
    "& span": {
      background: "linear-gradient(90deg, #00D4AA, #00B8FF, #00D4AA)",
      backgroundSize: "200% auto",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      animation: "$shimmer 3s linear infinite",
    },
  },
  leftSubtitle: {
    fontSize: "1rem",
    color: "rgba(255,255,255,0.5)",
    textAlign: "center",
    maxWidth: "380px",
    lineHeight: 1.7,
    animation: "$fadeInUp 1s ease-out 0.5s both",
  },

  // Badges de características
  badgesRow: {
    display: "flex",
    gap: "12px",
    marginTop: "36px",
    flexWrap: "wrap",
    justifyContent: "center",
    animation: "$fadeInUp 1s ease-out 0.7s both",
  },
  badge: {
    padding: "8px 18px",
    borderRadius: "20px",
    border: "1px solid rgba(0,212,170,0.25)",
    background: "rgba(0,212,170,0.07)",
    color: "#00D4AA",
    fontSize: "0.78rem",
    fontWeight: 500,
    backdropFilter: "blur(8px)",
    letterSpacing: "0.5px",
  },

  // Divisor vertical
  divider: {
    width: "1px",
    background: "linear-gradient(to bottom, transparent, rgba(0,212,170,0.3), transparent)",
    margin: "0",
    flexShrink: 0,
    "@media (max-width: 768px)": { display: "none" },
  },

  // --- Panel Derecho (Formulario) ---
  rightPanel: {
    flex: "0 0 42%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 50px",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 768px)": {
      flex: "1",
      padding: "30px 24px",
    },
  },

  glassCard: {
    width: "100%",
    maxWidth: "400px",
    background: "rgba(255,255,255,0.04)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "24px",
    padding: "48px 40px",
    boxShadow: "0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
    animation: "$slideInRight 0.8s ease-out",
    "@media (max-width: 480px)": { padding: "36px 24px" },
  },

  // Logo móvil: círculo contenedor + imagen interna sin recorte
  mobileLogoWrapper: {
    display: "none",
    justifyContent: "center",
    marginBottom: "24px",
    "@media (max-width: 768px)": { display: "flex" },
  },
  mobileLogoCircle: {
    width: "90px",
    height: "90px",
    borderRadius: "50%",
    border: "2px solid rgba(0,212,170,0.45)",
    background: "rgba(5,10,20,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 20px rgba(0,212,170,0.2)",
  },
  mobileLogoImg: {
    width: "75%",
    height: "75%",
    objectFit: "contain",
    borderRadius: 0,  // Sin recorte
  },

  cardHeader: {
    marginBottom: "36px",
    animation: "$fadeInUp 0.8s ease-out 0.2s both",
  },
  cardTitle: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: "#FFFFFF",
    marginBottom: "8px",
  },
  cardSubtitle: {
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.4)",
  },

  // Inputs personalizados — texto BLANCO EN NEGRITA visible
  inputWrapper: {
    marginBottom: "20px",
    animation: "$fadeInUp 0.8s ease-out 0.3s both",
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.06)",
      borderRadius: "12px",
      color: "#FFFFFF",
      "& fieldset": {
        borderColor: "rgba(255,255,255,0.15)",
        transition: "border-color 0.3s",
      },
      "&:hover fieldset": { borderColor: "rgba(0,212,170,0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#00D4AA", borderWidth: "1.5px" },
    },
    "& .MuiInputLabel-outlined": {
      color: "rgba(255,255,255,0.55)",
      fontWeight: 500,
      "&.Mui-focused": { color: "#00D4AA" },
      "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
    },
    // El texto que escribe el usuario — BLANCO + NEGRITA
    "& .MuiOutlinedInput-input": {
      color: "#FFFFFF !important",
      fontWeight: "700 !important",
      fontSize: "1rem",
      caretColor: "#00D4AA",
      WebkitTextFillColor: "#FFFFFF !important",
      "&::placeholder": { color: "rgba(255,255,255,0.35)", fontWeight: 400, opacity: 1 },
    },
    // Contrarrestar el fondo azul del autorrelleno de Chrome
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 100px rgba(8,15,30,0.98) inset !important",
      WebkitTextFillColor: "#FFFFFF !important",
      fontWeight: "700 !important",
      caretColor: "#00D4AA",
      transition: "background-color 9999s ease-in-out 0s",
    },
  },
  inputWrapperDelayed: {
    animation: "$fadeInUp 0.8s ease-out 0.4s both",
  },

  // Botón Ingresar
  submitBtn: {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #00D4AA 0%, #00B8A9 50%, #0099CC 100%)",
    color: "#0A0E1A",
    fontWeight: 700,
    fontSize: "1rem",
    letterSpacing: "1px",
    textTransform: "uppercase",
    border: "none",
    cursor: "pointer",
    marginTop: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 8px 25px rgba(0, 212, 170, 0.3)",
    animation: "$fadeInUp 0.8s ease-out 0.5s both",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 35px rgba(0, 212, 170, 0.5)",
      background: "linear-gradient(135deg, #00E8BB 0%, #00D4AA 50%, #00B0E8 100%)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
    "&.MuiButtonBase-root": {
      outline: "none",
    },
  },

  // Footer del card
  cardFooter: {
    marginTop: "28px",
    textAlign: "center",
    animation: "$fadeInUp 0.8s ease-out 0.6s both",
  },
  footerText: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.25)",
    letterSpacing: "0.5px",
  },
  footerAccent: {
    color: "rgba(0,212,170,0.7)",
    fontWeight: 600,
  },
}));

// Partículas decorativas generadas dinámicamente
const PARTICLES = [
  { size: 4, top: "15%", left: "8%",  delay: "0s",   duration: "7s"  },
  { size: 3, top: "70%", left: "5%",  delay: "1s",   duration: "9s"  },
  { size: 5, top: "40%", left: "20%", delay: "2s",   duration: "6s"  },
  { size: 2, top: "85%", left: "45%", delay: "0.5s", duration: "8s"  },
  { size: 4, top: "25%", left: "75%", delay: "3s",   duration: "11s" },
  { size: 3, top: "60%", left: "90%", delay: "1.5s", duration: "7s"  },
  { size: 6, top: "10%", left: "55%", delay: "2.5s", duration: "10s" },
  { size: 2, top: "80%", left: "70%", delay: "4s",   duration: "8s"  },
];

// ============================================================================

const Login = () => {
  const classes = useStyles();
  const [user, setUser] = useState({ email: "", password: "" });
  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />

      {/* ── Efectos de Fondo ───────────────────────────── */}
      <div className={classes.dotGrid} />
      <div className={classes.bgOrb1} />
      <div className={classes.bgOrb2} />
      <div className={classes.bgOrb3} />

      {PARTICLES.map((p, i) => (
        <div
          key={i}
          className={classes.particle}
          style={{
            width: p.size,
            height: p.size,
            top: p.top,
            left: p.left,
            animationName: "float",
            animationDuration: p.duration,
            animationDelay: p.delay,
            animationIterationCount: "infinite",
            animationTimingFunction: "ease-in-out",
          }}
        />
      ))}

      {/* ── Panel Izquierdo (Branding) ─────────────────── */}
      <div className={classes.leftPanel}>
        <div className={classes.logoWrapper}>
          <div className={classes.logoGlow} />
          <div className={classes.logoRing} />
          {/* logoCircle = círculo oscuro que contiene el escudo sin recortarlo */}
          <div className={classes.logoCircle}>
            <img
              src="/logo.png"
              alt="CRM LASIT"
              className={classes.logoImg}
            />
          </div>
        </div>

        <h1 className={classes.leftTitle}>
          CRM <span>LASIT</span>
        </h1>
        <p className={classes.leftSubtitle}>
          Plataforma de gestión inteligente de comunicaciones, campañas de WhatsApp y asistente de inteligencia artificial.
        </p>

        <div className={classes.badgesRow}>
          <span className={classes.badge}>IA Inteligente</span>
          <span className={classes.badge}>WhatsApp Multi-Linea</span>
          <span className={classes.badge}>Campanas Masivas</span>
        </div>
      </div>

      {/* Divisor */}
      <div className={classes.divider} />

      {/* ── Panel Derecho (Formulario) ─────────────────── */}
      <div className={classes.rightPanel}>
        <div className={classes.glassCard}>

          {/* Logo para móviles */}
          <div className={classes.mobileLogoWrapper}>
            <div className={classes.mobileLogoCircle}>
              <img src="/logo.png" alt="CRM LASIT" className={classes.mobileLogoImg} />
            </div>
          </div>

          <div className={classes.cardHeader}>
            <h2 className={classes.cardTitle}>Inicio de Sesión</h2>
            <p className={classes.cardSubtitle}>Ingresa tus credenciales para continuar</p>
          </div>

          <form noValidate onSubmit={handleSubmit}>
            <div className={classes.inputWrapper}>
              <TextField
                variant="outlined"
                required
                fullWidth
                id="email"
                label={i18n.t("login.form.email")}
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className={`${classes.inputWrapper} ${classes.inputWrapperDelayed}`}>
              <TextField
                variant="outlined"
                required
                fullWidth
                name="password"
                label={i18n.t("login.form.password")}
                id="password"
                type="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              fullWidth
              className={classes.submitBtn}
              disableElevation
            >
              {i18n.t("login.buttons.submit")}
            </Button>
          </form>

          <div className={classes.cardFooter}>
            <p className={classes.footerText}>
              CRM <span className={classes.footerAccent}>LASIT</span> © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
